import { Package } from "lucide-react";

import { useMedidaDoItem } from "../../../../hooks/useMedidaDoItem";
import { qtdDoItem } from "../../../../lib/unidadeVenda";
import { findProductPhoto, getImageUrl } from "../../lib/pedidoPhotos";

/** Lista de itens com fotos, subitems, etapas e observação por item. */
export function PedidoItensSection({ items = [], productsMap = {} }) {
  // Quilo/litro (30/09/2026): productsMap só tem fotos; a medida vem do cadastro.
  const medida = useMedidaDoItem();
  return (
    <div>
      <p className="text-xs font-semibold mb-1.5 flex items-center gap-1.5 text-muted-foreground">
        <Package className="w-3.5 h-3.5" /> Itens ({items.length})
      </p>
      <div className="divide-y divide-muted">
        {items.map((item, index) => (
          <ItemRow key={index} item={item} productsMap={productsMap} medida={medida(item)} />
        ))}
      </div>
    </div>
  );
}

function ItemRow({ item, productsMap, medida }) {
  const photoUrl =
    item.photo_url ||
    item.foto_url ||
    findProductPhoto(item.name || item.nome, productsMap) ||
    (item.id && productsMap[`id_${item.id}`]);
  const photoSrc = photoUrl ? getImageUrl(photoUrl) : null;
  const qty = Number(item.quantity || item.quantidade || 1);
  const price = Number(
    item.final_price ?? item.preco_unitario ?? item.unit_price ?? item.sale_price ?? item.preco ?? 0,
  );

  return (
    <div className="py-2 first:pt-0">
      <div className="flex gap-2 items-start">
        <Thumb src={photoSrc} size={10} />
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start">
            <p className="text-sm font-medium">
              {qtdDoItem(qty, medida)} {item.name || item.nome}
              {item.combo_type && (
                <span className="text-xs text-orange-500 ml-1">
                  ({item.combo_type === "combo" ? "Combo" : "Simples"})
                </span>
              )}
            </p>
            <p className="text-sm font-medium shrink-0 ml-2">R$ {(price * qty).toFixed(2).replace(".", ",")}</p>
          </div>
          {item.subitems?.length > 0 && <Subitems list={item.subitems} productsMap={productsMap} />}
          {(!item.subitems || item.subitems.length === 0) && item.etapas?.length > 0 && (
            <Etapas list={item.etapas} productsMap={productsMap} />
          )}
          {(item.observation || item.observacao) && (
            <p className="text-[10px] text-yellow-700 dark:text-yellow-400 mt-0.5 italic">
              * {item.observation || item.observacao}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function Subitems({ list, productsMap }) {
  return (
    <div className="mt-1 space-y-1">
      {list.map((sub, i) => {
        const subName = sub.nome || sub.name || "";
        const url = sub.photo_url || sub.foto_url || findProductPhoto(subName, productsMap);
        const src = url ? getImageUrl(url) : null;
        return (
          <div key={i} className="flex items-center gap-1.5">
            <Thumb src={src} size={6} />
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              <span className="text-orange-400">+ </span>
              {sub.step_name && <span className="opacity-70">{sub.step_name}: </span>}
              {subName}
              {sub.preco > 0 && <span className="text-orange-500"> (+R$ {sub.preco.toFixed(2).replace(".", ",")})</span>}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function Etapas({ list, productsMap }) {
  return (
    <div className="mt-1 space-y-1">
      {list.map((etapa, i) => (
        <div key={i}>
          {etapa.itens?.map((eiName, ei) => {
            const photo = findProductPhoto(eiName, productsMap);
            const src = photo ? getImageUrl(photo) : null;
            return (
              <div key={ei} className="flex items-center gap-1.5">
                <Thumb src={src} size={6} />
                <p className="text-[10px] text-muted-foreground leading-relaxed">
                  <span className="text-orange-400">+ </span>
                  <span className="opacity-70">{etapa.etapa}: </span>
                  {eiName}
                </p>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function Thumb({ src, size }) {
  const sz = size === 10 ? "w-10 h-10" : "w-6 h-6";
  const ic = size === 10 ? "w-4 h-4" : "w-2.5 h-2.5";
  return (
    <div className={`${sz} rounded bg-muted flex-shrink-0 overflow-hidden relative flex items-center justify-center`}>
      <Package className={`${ic} text-muted-foreground`} />
      {src && (
        <img
          src={src}
          alt=""
          className="w-full h-full object-cover absolute inset-0"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      )}
    </div>
  );
}
